//@flow
import * as Immutable from 'immutable';

import { MasterLocation } from 'common/models/flow/MasterLocation';
import type { MasterLocationModelType } from 'common/models/flow/MasterLocation';

import { StructuredAddress } from './StructuredAddress';
import { TeamId } from './TeamId';
import { LatLng } from './LatLng';

//Validation stuff
import {
    validatable,
    validate,
    validateAll,
    validateId,
    softValidateAll
} from 'common/utils/ValidationUtil';
import RegExUtil from 'common/utils/RegExUtil';
import NumberUtils from 'common/utils/NumberUtils';
import DataUtils from 'common/utils/DataUtils';

import { UserId } from './UserId';
import { VehicleModelId } from './VehicleModelId';
import { SkillId } from './SkillId';
import { MultiDaySlot } from './MultiDaySlot';

import { Contact } from './Contact';
import { ContactId } from './ContactId';

import { GeocodingMetadata } from './GeocodingMetadata';
import { AuditMetadata } from './AuditMetadata';
import { LocationLearningMetadata } from './LocationLearning';
import { LocationCrossDockInfo } from './LocationCrossDockInfo';

import { LocationId } from './LocationId';
import I18nHelper from 'common/helpers/I18nHelper';
import StringUtils from 'common/utils/StringUtils';
import EntityTypeEnum from 'common/enums/EntityTypeEnum';
import { TimeSlot } from './TimeSlot';
import EntityImportDataHelper from 'common/entity/helpers/EntityImportDataHelper';
import queryString from 'query-string';
import { ENTITY_NAME_ID } from 'common/constants/entity/locationMaster';

import { MasterVehicle } from 'common/models/flow/MasterVehicle';
import TimeUtils from 'common/utils/TimeUtils';

const translationsNamespace = ['entityManagement'];

const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

export const LocationTypeType = new EntityTypeEnum({
    Client: 'CLIENT',
    Kitchen: 'KITCHEN',
    Warehouse: 'WAREHOUSE',
    ManufacturingPlant: 'MANUFACTURING_PLANT',
    Supplier: 'SUPPLIER',
    Retailer: 'RETAILER',
    Homebase: 'HOMEBASE'
});

export const ConfidenceType = new EntityTypeEnum({
    High: 'HIGH',
    Medium: 'MEDIUM',
    Low: 'LOW'
});

export const GeocodingSourceType = new EntityTypeEnum({
    Client: 'CLIENT',
    Locus: 'LOCUS_GEOCODER',
    SystemLearnt: 'SYSTEM_LEARNT'
});

export const VerificationStatus = new EntityTypeEnum({
    Verified: 'verified',
    Unverified: 'unverified'
});

@validatable
class InternalMasterLocation extends MasterLocation {
    static fromJS(json: MasterLocationModelType): InternalMasterLocation {
        const state: Object = Object.assign({}, json);
        state.teams = Immutable.List(state.teams).map(item =>
            TeamId.fromJS(item)
        );
        state.address = state.address
            ? StructuredAddress.fromJS(state.address)
            : state.address;
        state.latLng = state.latLng
            ? LatLng.fromJS(state.latLng)
            : state.latLng;
        state.geocodingMetadata = state.geocodingMetadata
            ? GeocodingMetadata.fromJS(state.geocodingMetadata)
            : state.geocodingMetadata;
        state.contactId = state.contactId
            ? ContactId.fromJS(state.contactId)
            : state.contactId;
        state.contact = state.contact
            ? Contact.fromJS(state.contact)
            : state.contact;
        state.timeSlots = Immutable.List(state.timeSlots).map(item =>
            MultiDaySlot.fromJS(item)
        );
        state.users = Immutable.List(state.users).map(item =>
            UserId.fromJS(item)
        );
        state.skills = Immutable.List(state.skills).map(item =>
            SkillId.fromJS(item)
        );
        state.vehicleModels = Immutable.List(state.vehicleModels).map(item =>
            VehicleModelId.fromJS(item)
        );
        state.customProperties = Immutable.Map(state.customProperties);
        state.pincodes = Immutable.List(state.pincodes);
        state.auditMetadata = state.auditMetadata
            ? AuditMetadata.fromJS(state.auditMetadata)
            : state.auditMetadata;
        state.locationLearningAudit = Immutable.List(
            state.locationLearningAudit
        ).map(item => LocationLearningMetadata.fromJS(item));
        state.crossDockInfo = LocationCrossDockInfo.fromJS(state.crossDockInfo);
        state.vehicleModelId = state.vehicleModelId
            ? VehicleModelId.fromJS(state.vehicleModelId)
            : state.vehicleModelId;
        state.vehicleModel = state.vehicleModel
            ? MasterVehicle.fromJS(state.vehicleModel)
            : state.vehicleModel;
        return new this(Immutable.Map(state));
    }

    isSame(location: MasterLocation): boolean {
        return this.clientId === location.clientId && this.id === location.id;
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        const { address, latLng } = this;
        if (address && address.hasValidationErrors()) {
            return true;
        }
        if (latLng && latLng.hasValidationErrors()) {
            return true;
        }
        return this.validateId() || this.validateName();
    }

    @validateAll
    setAllValidationErrors(): this {
        let { address, latLng, timeSlots } = this;
        if (address) {
            address = address.setAllValidationErrors();
        } else {
            //validation when address completely is empty
            address = StructuredAddress.fromJS({}).setAllValidationErrors();
        }
        if (latLng) {
            latLng = latLng.setAllValidationErrors();
        }
        if (timeSlots) {
            timeSlots = timeSlots.map(timeSlot =>
                timeSlot.setAllValidationErrors()
            );
        }
        return this.setAddress(address)
            .setLatLng(latLng)
            .setTimeSlots(timeSlots);
    }

    @validate
    setId(id: string): this {
        return super.setId(id);
    }

    validateId() {
        return validateId(this.id);
    }

    @validate
    setCode(code: ?string): this {
        return super
            .setCode(code)
            .setId(!DataUtils.isEmpty(code) ? RegExUtil.conformToId(code) : '');
    }

    validateCode() {
        const code = this.code || this.id;
        if (StringUtils.isEmpty(code)) {
            return nsTranslate('entityValidationErrors.codeEmpty');
        }
        if (code.trim().length < 2) {
            return nsTranslate('entityValidationErrors.codeLessThan2');
        }
        if (code.trim().length > 32) {
            return nsTranslate('entityValidationErrors.codeGreaterThan32');
        }
    }

    setContact(contact: ?Contact): this {
        let updatedLocation = super.setContact(contact);
        if (contact) {
            updatedLocation = updatedLocation.setContactId(
                ContactId.fromJS({
                    clientId: contact.clientId,
                    contactId: contact.id
                })
            );
        } else {
            updatedLocation = updatedLocation.setContactId(null);
        }
        return updatedLocation;
    }

    @validate
    setName(name: string): this {
        return super.setName(name);
    }

    validateName() {
        if (
            StringUtils.isEmpty(this.name) ||
            StringUtils.isEmpty(this.name.trim())
        ) {
            return nsTranslate('entityValidationErrors.nameEmpty', {
                entityNameId: ENTITY_NAME_ID
            });
        }
    }

    @validate
    setType(type: string): this {
        return super.setType(type);
    }

    validateType() {
        let validationMessage = null;
        const locationTypeValues = LocationTypeType.values();
        if (!this.type) {
            return nsTranslate('entityValidationErrors.missingLocationType');
        }
        if (
            !this.type ||
            !this.type.trim() ||
            !locationTypeValues.includes(this.type)
        ) {
            validationMessage = nsTranslate(
                'entityValidationErrors.typeEnumValidation',
                {
                    typeList: locationTypeValues.join(', '),
                    entityNameId: ENTITY_NAME_ID
                }
            );
        }
        return validationMessage;
    }

    @validate
    setTransactionDuration(transactionDuration: number): this {
        return super.setTransactionDuration(transactionDuration);
    }

    validateTransactionDuration() {
        if (
            !DataUtils.isEmpty(this.transactionDuration) &&
            !NumberUtils.isWholeNumber(this.transactionDuration)
        ) {
            return nsTranslate(
                'locationValidationErrors.transactionDurationNotAWholeNumber'
            );
        }
    }

    @validate
    setPriority(priority: ?number): this {
        return super.setPriority(priority);
    }

    validatePriority(): ?string {
        const { priority } = this;
        if (
            !DataUtils.isEmpty(priority) &&
            !NumberUtils.isWholeNumber(priority)
        ) {
            return nsTranslate(
                'locationValidationErrors.priorityNotWholeNumber'
            );
        }
    }

    @validate
    setMultiDaySlotSpan(multiDaySlotSpan: ?number): this {
        return super.setMultiDaySlotSpan(multiDaySlotSpan);
    }

    validateMultiDaySlotSpan(): ?string {
        const { multiDaySlotSpan } = this;
        if (
            !DataUtils.isEmpty(multiDaySlotSpan) &&
            (!NumberUtils.isInteger(multiDaySlotSpan) ||
                !NumberUtils.isPositiveNumber(multiDaySlotSpan))
        ) {
            return nsTranslate(
                'locationValidationErrors.multiDaySlotSpanNotPositiveInteger'
            );
        }
    }

    sortTimeSlots(): this {
        return this.setTimeSlots(TimeSlot.sortTimeSlots(this.timeSlots));
    }

    initializeWeekSlots(): this {
        return this.setTimeSlots(
            TimeSlot.initializeAllTimeSlots(this.timeSlots)
        );
    }

    isVerified(): boolean {
        return this.geocodingMetadata && this.geocodingMetadata.verified;
    }

    getCellValidationMessage({ header, validationData }) {
        return EntityImportDataHelper.getCellValidationMessage({
            entity: this,
            header,
            validationData
        });
    }

    getLocationIdURL(clientId: string) {
        const query = queryString.stringify({
            edit: this.id
        });
        let entityType = 'location';
        if (this.type === 'CROSS_DOCK' || this.type === 'HOMEBASE') {
            entityType = 'homebase';
        }
        return {
            pathname: `/client/${clientId}/entity/${entityType}`,
            search: query
        };
    }

    get address(): ?StructuredAddress {
        return this._state.get('address');
    }

    get latLng(): ?LatLng {
        return this._state.get('latLng');
    }

    get geocodingMetadata(): ?GeocodingMetadata {
        return this._state.get('geocodingMetadata');
    }

    get contactId(): ?ContactId {
        return this._state.get('contactId');
    }

    get contact(): ?Contact {
        return this._state.get('contact');
    }

    get teams(): Immutable.List<TeamId> {
        return this._state.get('teams');
    }

    get timeSlots(): Immutable.List<MultiDaySlot> {
        return this._state.get('timeSlots');
    }

    get users(): Immutable.List<UserId> {
        return this._state.get('users');
    }

    get skills(): Immutable.List<SkillId> {
        return this._state.get('skills');
    }

    get vehicleModels(): Immutable.List<VehicleModelId> {
        return this._state.get('vehicleModels');
    }

    extractLocationId(): LocationId {
        return LocationId.fromJS({
            clientId: this.clientId,
            locationId: this.id,
            version: this.version
        });
    }

    sanitize({ latLng = true }: Object = {}): this {
        const {
            latLng: latLngValue,
            contact: contactValue,
            contactId,
            timeSlots,
            crossDockInfo
        } = this;
        let updatedLocation = this;
        if (latLng && latLngValue) {
            const sanitizedLatLng = latLngValue.defaultSanitize();
            updatedLocation = updatedLocation.setLatLng(sanitizedLatLng);
        }

        if (crossDockInfo) {
            const sanitizedCrossDockInfo = crossDockInfo.defaultSanitize();
            updatedLocation = updatedLocation.setCrossDockInfo(
                sanitizedCrossDockInfo
            );
        }

        if (
            (DataUtils.isEmpty(contactValue?.id) &&
                DataUtils.isEmpty(contactValue?.name) &&
                DataUtils.isEmpty(contactValue?.type) &&
                DataUtils.isEmpty(contactValue?.phoneNumber?.phoneNumber) &&
                !contactValue?.teams?.size) ||
            (contactValue?.id && contactId?.contactId)
        ) {
            //using 'set' instead of 'setContact' due to side effect on setContactId
            updatedLocation = updatedLocation.set('contact', null);
        }

        if (timeSlots) {
            const sanitizedTimeSlots = timeSlots.filter(
                timeSlot => timeSlot.slots.size
            );
            updatedLocation = updatedLocation.setTimeSlots(sanitizedTimeSlots);
        }

        updatedLocation = updatedLocation.setVehicleModel(null);

        return updatedLocation;
    }

    setHiddenModelProperties(hiddenModelProperties: Immutable.List<string>) {
        return this.clone(
            this._state.set('hiddenModelProperties', hiddenModelProperties)
        );
    }

    getHiddenModelProperties(): Immutable.List<string> {
        return this._state.get('hiddenModelProperties');
    }

    isHidden(key: string) {
        const hiddenModelProperties = this.getHiddenModelProperties();
        const [groupKey] = key.split('.');
        return (
            (hiddenModelProperties && hiddenModelProperties.includes(key)) ||
            (hiddenModelProperties && hiddenModelProperties.includes(groupKey))
        );
    }

    // Alternative to lazy loading of nested models (init and cleaning)
    defaultSanitize(): ?this {
        let sanitizedLocation = this.sanitize();
        sanitizedLocation = super.defaultSanitize(sanitizedLocation);

        if (sanitizedLocation) {
            const sanitizedContact =
                sanitizedLocation.contact?.defaultSanitize() || null;
            const sanitizedLatLng =
                sanitizedLocation.latLng &&
                sanitizedLocation.latLng.defaultSanitize();
            return (
                sanitizedLocation
                    //using 'set' instead of 'setContact' due to side effect on setContactId
                    .set('contact', sanitizedContact)
                    .setLatLng(sanitizedLatLng)
            );
        }
        return sanitizedLocation;
    }

    static defaultInit({ props }): InternalMasterLocation {
        const address = StructuredAddress.fromJS({
            countryCode: props.defaultCountryCode
        });
        const contact = Contact.defaultInit({ props });
        const latLng = LatLng.defaultInit({ props });

        return InternalMasterLocation.fromJS({
            type: 'CLIENT'
        })
            .setAddress(address)
            .set('contact', contact) //using 'set' instead of 'setContact' due to side effect on setContactId
            .setLatLng(latLng)
            .setPriority(1);
    }

    get auditMetadata(): ?AuditMetadata {
        return this._state.get('auditMetadata');
    }

    get locationLearningAudit(): Immutable.List<LocationLearningMetadata> {
        return this._state.get('locationLearningAudit');
    }

    get vehicleModelId(): ?VehicleModelId {
        return this._state.get('vehicleModelId');
    }

    get vehicleModel(): ?MasterVehicle {
        return this._state.get('vehicleModel');
    }

    getNextTimeSlot(): void | TimeSlot {
        const todayTimeSlots =
            MultiDaySlot.getTodayTimeSlots(this.timeSlots) ?? [];

        const {
            start: expectedStartTime,
            end: expectedEndTime
        } = TimeSlot.expectedStartEndTime();

        // This check is validate scenario of multi-day.
        // which we are not supporting right now in showing slot suggestions
        if (
            TimeUtils.isToday(expectedEndTime) &&
            TimeUtils.isToday(expectedStartTime)
        ) {
            const expectedEndTimeInSlotFormat = expectedEndTime.format('HH:mm');
            const expectedStartTimeInSlotFormat = expectedStartTime.format(
                'HH:mm'
            );
            const nextSlot = todayTimeSlots.find(
                slot => slot.end > expectedEndTimeInSlotFormat
            );

            if (nextSlot) {
                if (nextSlot.start > expectedStartTimeInSlotFormat) {
                    return nextSlot;
                } else {
                    const updatedSlot = nextSlot.setStart(
                        expectedStartTimeInSlotFormat
                    );
                    return updatedSlot;
                }
            }
        }

        return;
    }
}

export { InternalMasterLocation as MasterLocation };
